import { Head } from 'vite-react-ssg';
import { Link } from "react-router-dom";
import { ArrowRight, MessageSquare, Shield, Zap, Code, ExternalLink, Check } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Breadcrumbs from "@/components/Breadcrumbs";

const DeepseekDeutsch = () => {
  const faqItems = [
    {
      question: "Was ist DeepSeek auf Deutsch?",
      answer: "DeepSeek ist ein chinesisches KI-Unternehmen aus Hangzhou, das leistungsstarke Sprachmodelle entwickelt. Aktuell werden über die API DeepSeek-V4-Flash und DeepSeek-V4-Pro angeboten. Die Modelle verstehen und generieren fließend Deutsch und sind eine kostengünstige Alternative zu ChatGPT und Claude."
    },
    {
      question: "Ist DeepSeek in Deutschland kostenlos nutzbar?",
      answer: "Ja. Der DeepSeek-Chat auf chat.deepseek.com ist für Privatnutzer in Deutschland kostenlos. Nur die API-Nutzung für Entwickler ist kostenpflichtig — ab $0,14 pro 1 Million Input-Tokens (Cache-Miss) bei DeepSeek-V4-Flash."
    },
    {
      question: "Ist DeepSeek DSGVO-konform?",
      answer: "DeepSeek verarbeitet Daten auf Servern in China, einem Land ohne EU-Angemessenheitsbeschluss. Eine Übermittlung personenbezogener Daten benötigt daher Standardvertragsklauseln plus eine dokumentierte Transfer-Folgenabschätzung. Für DSGVO-sensible Fälle empfiehlt sich Eigenhosting der offenen Gewichte in einer EU-Region (z. B. AWS eu-central-1 Frankfurt) statt der gehosteten API."
    },
    {
      question: "Wie melde ich mich bei DeepSeek an?",
      answer: "Gehen Sie auf chat.deepseek.com, klicken Sie auf 'Anmelden' und registrieren Sie sich mit E-Mail-, Google- oder Apple-Konto. Die Anmeldung ist kostenlos und dauert unter einer Minute. Die Anmeldung erfolgt ausschließlich bei DeepSeek selbst — nicht auf dieser unabhängigen Info-Seite."
    },
    {
      question: "Welche DeepSeek-Modelle gibt es 2026?",
      answer: "Über die offizielle API sind DeepSeek-V4-Flash (schnell und günstig) und DeepSeek-V4-Pro (stärkstes Modell für Mathe, Code und Logik) verfügbar. Die älteren Endpunkte deepseek-chat (V3) und deepseek-reasoner (R1) wurden am 24. Juli 2026 abgeschaltet; die offenen Gewichte der Vorgängermodelle bleiben zum Eigenhosten verfügbar."
    },
    {
      question: "Kann ich DeepSeek geschäftlich nutzen?",
      answer: "Ja. Die Lizenz erlaubt kommerzielle Nutzung — über die API oder durch Eigenhosting der Open-Source-Modelle. Prüfen Sie bei personenbezogenen Daten vorher die DSGVO-Anforderungen und die Transparenzpflichten der EU-KI-Verordnung."
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://deepseek.ai/deepseek-deutsch",
        "url": "https://deepseek.ai/deepseek-deutsch",
        "name": "DeepSeek auf Deutsch — Kostenlos Nutzen, Anleitung & API",
        "description": "Vollständiger Leitfaden zu DeepSeek auf Deutsch: Anmeldung, kostenloser Chat, DSGVO, API und Vergleich mit ChatGPT.",
        "inLanguage": "de"
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqItems.map(item => ({
          "@type": "Question",
          "name": item.question,
          "acceptedAnswer": { "@type": "Answer", "text": item.answer }
        }))
      }
    ]
  };

  return (
    <>
      <Head>
        <html lang="de" />
        <title>DeepSeek auf Deutsch (2026): Zugang, Datenschutz &amp; API-Preise</title>
        <meta name="description" content="DeepSeek auf Deutsch: kostenloser Chat, Anmeldung in 30 Sekunden, DSGVO-Hinweise, API-Preise ab $0,14/1M Tokens und Vergleich mit ChatGPT." />
        <meta name="keywords" content="deepseek deutsch, deepseek deutschland, deepseek anmelden, deepseek kostenlos, deepseek api deutsch, deepseek dsgvo" />
        <link rel="canonical" href="https://deepseek.ai/deepseek-deutsch" />
        <link rel="alternate" hrefLang="de" href="https://deepseek.ai/deepseek-deutsch" />
        <link rel="alternate" hrefLang="en" href="https://deepseek.ai/" />
        <link rel="alternate" hrefLang="x-default" href="https://deepseek.ai/" />
        <meta property="og:title" content="DeepSeek auf Deutsch: Zugang, Datenschutz & API-Preise" />
        <meta property="og:description" content="DeepSeek-V3 und R1 auf Deutsch nutzen: Anleitung, DSGVO und API-Preise." />
        <meta property="og:url" content="https://deepseek.ai/deepseek-deutsch" />
        <meta property="og:locale" content="de_DE" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DeepSeek auf Deutsch: Zugang, Datenschutz & API-Preise" />
        <meta name="twitter:description" content="DeepSeek-V3 und R1 auf Deutsch nutzen: Anleitung, DSGVO und API-Preise." />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Head>

      <main className="min-h-screen bg-background">
        <section className="relative py-20 bg-gradient-to-br from-primary/5 via-background to-primary/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Breadcrumbs currentPage="DeepSeek auf Deutsch" />
            <div className="text-center mt-8">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                DeepSeek auf Deutsch
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto mb-8">
                Der vollständige deutsche Leitfaden zum DeepSeek-KI-Chat — kostenlos nutzen, anmelden, DSGVO verstehen und die API integrieren.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="https://chat.deepseek.com" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90">
                  <MessageSquare className="w-5 h-5 mr-2" /> DeepSeek Chat starten <ExternalLink className="w-4 h-4 ml-2" />
                </a>
                <Link to="/deepseek-api" className="inline-flex items-center justify-center px-6 py-3 rounded-lg border border-border bg-background font-semibold hover:bg-muted">
                  <Code className="w-5 h-5 mr-2" /> API-Dokumentation
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground mb-6">Was ist DeepSeek?</h2>
            <p className="text-lg text-muted-foreground mb-4">
              DeepSeek ist ein chinesisches KI-Unternehmen mit Sitz in Hangzhou, das seit 2023 leistungsstarke Open-Source-Sprachmodelle entwickelt. Die Modelle <strong className="text-foreground">DeepSeek-V3</strong> und <strong className="text-foreground">DeepSeek-R1</strong> stehen in Benchmarks auf Augenhöhe mit GPT-4o und Claude Sonnet — bei einem Bruchteil der Kosten.
            </p>
            <p className="text-lg text-muted-foreground">
              Für deutschsprachige Nutzer ist DeepSeek besonders interessant, weil die Modelle fließend Deutsch beherrschen, der Chat kostenlos ist und die API rund 90 % günstiger als OpenAI ausfällt.
            </p>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">DeepSeek in 3 Schritten anmelden</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { n: 1, t: "Webseite öffnen", d: "Gehen Sie auf chat.deepseek.com — keine Installation nötig, läuft im Browser." },
                { n: 2, t: "Konto erstellen", d: "Registrieren Sie sich kostenlos mit E-Mail, Google- oder Apple-Konto." },
                { n: 3, t: "Auf Deutsch chatten", d: "Tippen Sie Ihre Frage auf Deutsch ein — DeepSeek antwortet sofort in Ihrer Sprache." },
              ].map(s => (
                <div key={s.n} className="text-center">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold mx-auto mb-4">{s.n}</div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{s.t}</h3>
                  <p className="text-muted-foreground">{s.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">DeepSeek-Modelle im Überblick</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { name: "DeepSeek-V4-Flash", tag: "Allgemeiner Chat", bullets: ["128K Kontextfenster", "Schnelle Antworten", "$0,14 Input / $0,28 Output je 1M Tokens"] },
                { name: "DeepSeek-V4-Pro", tag: "Komplexe Aufgaben & Logik", bullets: ["Stärkstes aktuelles Modell", "Mathe, Code & Logik", "$0,435 Input / $0,87 Output je 1M Tokens"] },
                { name: "V3 / R1 / Coder", tag: "Abgeschaltet", bullets: ["deepseek-chat & deepseek-reasoner", "am 24. Juli 2026 eingestellt", "Preise gelten nicht mehr"] },
              ].map(m => (
                <div key={m.name} className="p-6 rounded-xl bg-card border border-border">
                  <h3 className="text-xl font-bold text-foreground mb-2">{m.name}</h3>
                  <p className="text-muted-foreground text-sm mb-4">{m.tag}</p>
                  <ul className="space-y-2">
                    {m.bullets.map(b => <li key={b} className="flex items-start text-sm text-muted-foreground"><Check className="w-4 h-4 text-primary mr-2 mt-0.5 flex-shrink-0" />{b}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-start gap-4 p-6 rounded-xl bg-card border border-border">
              <Shield className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-3">DSGVO & Datenschutz</h2>
                <p className="text-muted-foreground mb-3">
                  DeepSeek verarbeitet Eingaben standardmäßig auf Servern in der Volksrepublik China. Für die <strong>DSGVO</strong> bedeutet das: Ohne explizite Einwilligung dürfen Sie <strong>keine personenbezogenen Daten Dritter</strong> (z. B. Kundendaten, Mitarbeiterdaten) in den Chat oder die API eingeben.
                </p>
                <p className="text-muted-foreground">
                  Für sensible geschäftliche Anwendungsfälle empfehlen wir das Eigenhosting des Open-Source-Modells DeepSeek-V3 auf einem EU-Server, oder die Nutzung über OpenRouter mit europäischer Datenverarbeitung. Mehr Details auf unserer{" "}
                  <Link to="/is-deepseek-safe" className="text-primary hover:underline">Sicherheitsanalyse-Seite</Link>.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground mb-4">EU-KI-Verordnung: was Unternehmen in der DACH-Region beachten müssen</h2>
            <p className="text-muted-foreground mb-6">
              Anders als in den USA oder Kanada gilt in Deutschland, Österreich und der Schweiz (via EU-Marktzugang) zusätzlich die KI-Verordnung der EU. Sie richtet sich nicht nur an Modellanbieter, sondern auch an Sie als Betreiber:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start text-muted-foreground"><Check className="w-4 h-4 text-primary mr-2 mt-1 flex-shrink-0" /><span><strong className="text-foreground">Transparenzpflicht:</strong> Nutzer müssen erkennen können, dass sie mit einer KI sprechen. Kennzeichnen Sie Chatbots und KI-generierte Inhalte eindeutig.</span></li>
              <li className="flex items-start text-muted-foreground"><Check className="w-4 h-4 text-primary mr-2 mt-1 flex-shrink-0" /><span><strong className="text-foreground">Hochrisiko-Anwendungen:</strong> Bewerberauswahl, Kreditwürdigkeit, Bildungsbewertung oder Zugang zu öffentlichen Leistungen unterliegen strengen Anforderungen — hier ist die gehostete API in der Regel keine tragfähige Grundlage.</span></li>
              <li className="flex items-start text-muted-foreground"><Check className="w-4 h-4 text-primary mr-2 mt-1 flex-shrink-0" /><span><strong className="text-foreground">KI-Kompetenz:</strong> Beschäftigte, die KI-Systeme im Unternehmen einsetzen, sollen dafür geschult werden — dokumentieren Sie interne Richtlinien und Schulungen.</span></li>
              <li className="flex items-start text-muted-foreground"><Check className="w-4 h-4 text-primary mr-2 mt-1 flex-shrink-0" /><span><strong className="text-foreground">Verzeichnis von Verarbeitungstätigkeiten:</strong> KI-Tools mit Personenbezug gehören nach Art. 30 DSGVO ins Verzeichnis, inklusive Drittlandtransfer nach China.</span></li>
            </ul>
            <p className="text-sm text-muted-foreground">
              Allgemeine Informationen zu öffentlich dokumentierten Pflichten, keine Rechtsberatung — für den konkreten Fall entscheidet Ihre Datenschutzbeauftragte.
            </p>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground mb-4">DeepSeek mit EU-Datenhaltung betreiben</h2>
            <p className="text-muted-foreground mb-6">
              Weil die Modellgewichte offen sind, lässt sich DeepSeek ohne Datenübermittlung nach China betreiben. Drei praxistaugliche Wege:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start text-muted-foreground"><Check className="w-4 h-4 text-primary mr-2 mt-1 flex-shrink-0" /><span><strong className="text-foreground">Eigenhosting in einer EU-Region</strong> — AWS eu-central-1 (Frankfurt), Azure Germany West Central oder ein deutscher Anbieter wie Hetzner/IONOS, betrieben mit vLLM oder SGLang.</span></li>
              <li className="flex items-start text-muted-foreground"><Check className="w-4 h-4 text-primary mr-2 mt-1 flex-shrink-0" /><span><strong className="text-foreground">EU-Inferenzanbieter</strong> — Dienste mit europäischer Datenverarbeitung und Auftragsverarbeitungsvertrag (AVV) nach Art. 28 DSGVO.</span></li>
              <li className="flex items-start text-muted-foreground"><Check className="w-4 h-4 text-primary mr-2 mt-1 flex-shrink-0" /><span><strong className="text-foreground">Kompromiss beachten:</strong> Selbst gehostet laufen offene Vorgängergewichte, nicht der aktuelle V4-Pro-Stand der offiziellen API — die Qualität liegt also etwas darunter.</span></li>
            </ul>
            <p className="text-sm text-muted-foreground">
              Kostenvergleich zwischen gehosteter API und Eigenhosting finden Sie auf unserer <Link to="/pricing" className="text-primary hover:underline">Preisseite</Link>.
            </p>
          </div>
        </section>


        <section className="py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">Häufig gestellte Fragen</h2>
            <Accordion type="single" collapsible className="space-y-4">
              {faqItems.map((item, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="bg-card border border-border rounded-lg px-6">
                  <AccordionTrigger className="text-foreground font-semibold text-left hover:no-underline">{item.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{item.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <section className="py-20 bg-primary">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-6">DeepSeek jetzt auf Deutsch testen</h2>
            <p className="text-xl text-primary-foreground/90 mb-8">Kostenlos, keine Kreditkarte erforderlich.</p>
            <a href="https://chat.deepseek.com" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 rounded-lg bg-white text-primary font-semibold hover:bg-gray-100">
              <Zap className="w-5 h-5 mr-2" /> Kostenlos starten <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </div>
        </section>
      </main>
    </>
  );
};

export default DeepseekDeutsch;
